/**
 * Error Handling Types
 *
 * Provides unified error types and type guards for consistent error handling
 * across the application.
 */

import type { PostgrestError } from '@supabase/supabase-js'

/**
 * Unified API Error interface
 * Standardizes error responses across all API endpoints
 */
export interface ApiError {
  message: string
  statusCode: number
  code?: string
  details?: Record<string, unknown>
  timestamp?: string
}

/**
 * Error response wrapper
 * Use this for all API error responses
 */
export interface ErrorResponse {
  error: ApiError
}

/**
 * Supabase PostgrestError with type safety
 * Extended to include common error properties
 */
export interface PostgrestErrorExtended extends PostgrestError {
  message: string
  details: string
  hint: string
  code: string
}

/**
 * Type guard to check if an error is a PostgrestError
 */
export function isPostgrestError(error: unknown): error is PostgrestError {
  return (
    typeof error === 'object'
    && error !== null
    && 'message' in error
    && 'details' in error
    && 'hint' in error
    && 'code' in error
  )
}

/**
 * Type guard to check if an error is a standard Error
 */
export function isStandardError(error: unknown): error is Error {
  return error instanceof Error
}

/**
 * Type guard to check if an error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object'
    && error !== null
    && 'message' in error
    && 'statusCode' in error
    && typeof (error as Record<string, unknown>).message === 'string'
    && typeof (error as Record<string, unknown>).statusCode === 'number'
  )
}

/**
 * Type guard to check if response has an error
 */
export function hasErrorResponse(response: unknown): response is ErrorResponse {
  return (
    typeof response === 'object'
    && response !== null
    && 'error' in response
    && isApiError((response as Record<string, unknown>).error)
  )
}

/**
 * Convert any error to ApiError format
 */
export function toApiError(
  error: unknown,
  defaultMessage = 'An unexpected error occurred',
  defaultStatusCode = 500,
): ApiError {
  // Already an ApiError
  if (isApiError(error))
    return error

  // PostgrestError
  if (isPostgrestError(error)) {
    return {
      message: error.message || defaultMessage,
      statusCode: defaultStatusCode,
      code: error.code,
      details: {
        hint: error.hint,
        details: error.details,
      },
      timestamp: new Date().toISOString(),
    }
  }

  // Standard Error
  if (isStandardError(error)) {
    return {
      message: error.message || defaultMessage,
      statusCode: defaultStatusCode,
      timestamp: new Date().toISOString(),
    }
  }

  // Object with message property
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const errorObj = error as Record<string, unknown>

    return {
      message: String(errorObj.message) || defaultMessage,
      statusCode: (typeof errorObj.statusCode === 'number' ? errorObj.statusCode : defaultStatusCode),
      code: (typeof errorObj.code === 'string' ? errorObj.code : undefined),
      timestamp: new Date().toISOString(),
    }
  }

  // Fallback for unknown error types
  return {
    message: defaultMessage,
    statusCode: defaultStatusCode,
    details: { originalError: String(error) },
    timestamp: new Date().toISOString(),
  }
}

/**
 * Sanitize error for production logging
 * Removes sensitive information like stack traces, connection strings, etc.
 */
export function sanitizeError(error: unknown, isProduction: boolean): Record<string, unknown> {
  const apiError = toApiError(error)

  if (!isProduction) {
    // In development, include all details
    return {
      message: apiError.message,
      statusCode: apiError.statusCode,
      code: apiError.code,
      details: apiError.details,
      timestamp: apiError.timestamp,
      stack: isStandardError(error) ? error.stack : undefined,
    }
  }

  // In production, sanitize sensitive information
  return {
    message: apiError.message,
    statusCode: apiError.statusCode,
    code: apiError.code,
    timestamp: apiError.timestamp,

    // Exclude: stack traces, detailed error messages, internal details
  }
}

/**
 * Common error codes for the application
 */
export const ErrorCodes = {
  // Authentication errors (401)
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  AUTH_INVALID: 'AUTH_INVALID',
  AUTH_EXPIRED: 'AUTH_EXPIRED',

  // Authorization errors (403)
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  FORBIDDEN: 'FORBIDDEN',

  // Not found errors (404)
  NOT_FOUND: 'NOT_FOUND',
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',

  // Validation errors (400)
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INVALID_INPUT: 'INVALID_INPUT',

  // Rate limiting (429)
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',

  // Server errors (500)
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  DATABASE_ERROR: 'DATABASE_ERROR',
  EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR',
} as const

export type ErrorCode = typeof ErrorCodes[keyof typeof ErrorCodes]

/**
 * Create a standardized API error
 */
export function createApiError(
  message: string,
  statusCode: number,
  code?: ErrorCode,
  details?: Record<string, unknown>,
): ApiError {
  return {
    message,
    statusCode,
    code,
    details,
    timestamp: new Date().toISOString(),
  }
}
